import { BetType } from '@bpos/common';
import { ParticipantBetType } from '@bpos/v1';
import { BettingOfferType, SlipOffer, SlipOfferStatus, Selection as SlipSelection } from '@bpos/v1/over-ask';
import { calcDiff } from '@frontend/sports/common/core/utils/collection';
import { OddsOperations, convertFromBposOddsV4, emptyOdds, isEqual } from '@frontend/sports/odds/feature';
import { Decimal } from 'decimal.js';
import { isEmpty } from 'lodash-es';

import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../../core/picks/betslip-v2-option-market-pick';
import { BetslipV2ParticipantPick } from '../../../core/picks/betslip-v2-participant-pick';
import { BetslipV2WinParticipantPick } from '../../../core/picks/betslip-v2-win-participant-pick';
import { IBasePrice, IPrice, PriceType } from '../../../core/picks/pick-models';
import {
    BetslipV2HorseRaceOptionMarketPick,
    BetslipV2OptionMarketXCastRacePick,
} from '../../../core/picks/sport-specific/betslip-v2-horse-race-picks';
import { isBaseBetslipGroupPick } from '../../../core/utils';
import { OverAskBet, OverAskMarketTypeChange, OverAskPick, OverAskPickChanges } from '../../../model/over-ask/over-ask';

export class OverAskCounterOfferUpdates {
    static updateBetsWithChanges(overAskBets: OverAskBet[], slipOffers: SlipOffer[]): OverAskBet[] {
        const bets: OverAskBet[] = [];
        for (const bet of overAskBets) {
            const counterOffers = slipOffers.filter((so) => so.originalSlipKey);
            if (counterOffers.length === 0) {
                bets.push(bet);
                continue;
            }
            if (counterOffers.length > 1) {
                throw new Error('Split is not supported');
            }
            const offer = counterOffers[0];
            switch (offer.status) {
                case SlipOfferStatus.Accepted:
                case SlipOfferStatus.Modified:
                    bets.push(this.modifyBet(bet, offer));
                    break;
                case SlipOfferStatus.Declined:
                    break;
            }
        }

        return bets;
    }

    private static modifyBet(overAskBet: OverAskBet, offer: SlipOffer): OverAskBet {
        const bet = { ...overAskBet };
        if (!bet.stake.equals(offer.slip.stake)) {
            bet.changes = {
                stake: new Decimal(offer.slip.stake),
            };
        }

        if (!isEmpty(offer.slip.selections)) {
            const selections = Object.values(offer.slip.selections);
            bet.picks = bet.picks.map((p) => {
                const [pick, selection]: [OverAskPick, SlipSelection] = OverAskCounterOfferMatcher.matchSelection(p, selections);

                return OverAskCounterOfferPickUpdater.setChanges(pick, selection);
            });
        }

        return bet;
    }
}

export class OverAskCounterOfferMatcher {
    static matchSelection(overAskPick: OverAskPick, selections: SlipSelection[]): [OverAskPick, SlipSelection] {
        const pick = overAskPick.pick;
        let selection: SlipSelection | undefined;
        if (BetslipV1Pick.isPick(pick)) {
            selection = selections.find((s) => s.bettingOfferType === BettingOfferType.Result && s.resultPickDetails.pickId === pick.id.optionId);
        } else if (BetslipV2ParticipantPick.isPick(pick)) {
            selection = OverAskCounterOfferMatcher.findParticipantSelection(pick, selections);
        } else if (BetslipV2OptionMarketXCastRacePick.isPick(pick)) {
            selection = OverAskCounterOfferMatcher.findXcastOptionSelection(pick, selections);
        } else if (BetslipV2OptionMarketPick.isPick(pick)) {
            selection = selections.find(
                (s) =>
                    s.bettingOfferType === BettingOfferType.Option &&
                    pick.currentPrice &&
                    s.optionPickDetails.optionPicks[0].pickId === pick.currentPrice.id,
            );
        } else if (isBaseBetslipGroupPick(pick)) {
            const legs = pick.getLegs();
            selection = selections.find((s) => this.getFixureId(s) === legs[0].id.fixtureId);
        }
        if (!selection) {
            throw new Error(`Selection for pick ${pick.id.toString()} not found`);
        }

        return [overAskPick, selection];
    }

    private static getFixureId(selection: SlipSelection): string {
        return selection.partitionId > 1 ? `${selection.partitionId}:${selection.fixtureId}` : selection.fixtureId.toString();
    }

    private static findParticipantSelection(pick: BetslipV2ParticipantPick, selections: SlipSelection[]): SlipSelection | undefined {
        for (const selection of selections) {
            if (selection.bettingOfferType === BettingOfferType.Participant) {
                switch (selection.participantPickDetails.betType) {
                    case ParticipantBetType.Forecast:
                    case ParticipantBetType.Tricast:
                    case ParticipantBetType.CombinationForecast:
                    case ParticipantBetType.CombinationTricast:
                        // Multiple participants
                        const diff = calcDiff(
                            selection.participantPickDetails.picks.map((p) => p.pickId),
                            pick.prices.map((p) => p.id),
                        );
                        if (diff.added.length === 0 && diff.removed.length === 0) {
                            // Equal participants.
                            return selection;
                        }
                        break;
                    case ParticipantBetType.Win:
                    case ParticipantBetType.EachWay:
                    case ParticipantBetType.Place:
                        // Single participant ( win bet )
                        if (
                            BetslipV2WinParticipantPick.isPick(pick) &&
                            pick.currentPrice &&
                            pick.prices.map((p) => p.id).includes(selection.participantPickDetails.picks[0].pickId)
                        ) {
                            return selection;
                        }
                        break;
                }
            }
        }

        return undefined;
    }

    private static findXcastOptionSelection(pick: BetslipV2OptionMarketXCastRacePick, selections: SlipSelection[]): SlipSelection | undefined {
        for (const selection of selections) {
            if (selection.bettingOfferType === BettingOfferType.Option) {
                switch (selection.optionPickDetails.betType) {
                    case BetType.Forecast:
                    case BetType.Tricast:
                    case BetType.CombinationForecast:
                    case BetType.CombinationTricast:
                        // Multiple participants
                        const diff = calcDiff(
                            selection.optionPickDetails.optionPicks.map((p) => p.pickId),
                            pick.prices.map((p) => p.id),
                        );
                        if (diff.added.length === 0 && diff.removed.length === 0) {
                            // Equal participants.
                            return selection;
                        }
                        break;
                }
            }
        }

        return undefined;
    }
}

export class OverAskCounterOfferPickUpdater {
    static setChanges(overAskPick: OverAskPick, selection: SlipSelection): OverAskPick {
        const changes: OverAskPickChanges = {};
        const priceChange = OverAskCounterOfferPickUpdater.getPickPriceChange(overAskPick, selection);
        if (priceChange) {
            changes.price = priceChange;
        }
        const marketTypeChange = OverAskCounterOfferPickUpdater.getMarketTypeChange(overAskPick, selection);
        if (marketTypeChange) {
            changes.marketType = marketTypeChange;
        }

        return {
            ...overAskPick,
            changes,
        };
    }

    static getPickPriceChange(overAskPick: OverAskPick, selection: SlipSelection): IBasePrice | undefined {
        const overAskPrice = overAskPick.price;
        const pick = overAskPick.pick;
        if (selection.bettingOfferType === BettingOfferType.Participant) {
            if (BetslipV2WinParticipantPick.isPick(pick)) {
                // Price changes in V2 win participant pick
                if (overAskPrice.type === PriceType.Fixed && selection.participantPickDetails.isStartingPrice) {
                    // Switch from fixed to starting price.
                    return {
                        id: selection.participantPickDetails.picks[0].pickId,
                        type: PriceType.StartingPrice,
                        nativeOdds: emptyOdds,
                    } as IPrice;
                }
                if (overAskPrice.type === PriceType.StartingPrice && !selection.participantPickDetails.isStartingPrice) {
                    // Switch from starting price to fixed price
                    return {
                        id: selection.participantPickDetails.picks[0].pickId,
                        type: PriceType.Fixed,
                        nativeOdds: convertFromBposOddsV4(selection.odds),
                    } as IPrice;
                }
            }
        }
        if (selection.bettingOfferType === BettingOfferType.Option) {
            if (BetslipV2HorseRaceOptionMarketPick.isPick(pick)) {
                // Price changes in V2 horse option market pick
                if (overAskPrice.type === PriceType.Fixed && selection.optionPickDetails.isStartingPrice) {
                    // Switch from fixed to starting price.
                    return {
                        id: selection.optionPickDetails.optionPicks[0].pickId,
                        type: PriceType.StartingPrice,
                        nativeOdds: emptyOdds,
                    } as IPrice;
                }
                if (overAskPrice.type === PriceType.StartingPrice && !selection.optionPickDetails.isStartingPrice) {
                    // Switch from starting price to fixed price
                    return {
                        id: selection.optionPickDetails.optionPicks[0].pickId,
                        type: PriceType.Fixed,
                        nativeOdds: convertFromBposOddsV4(selection.odds),
                    } as IPrice;
                }
            }
        }
        if (overAskPrice.type === PriceType.Fixed && !isBaseBetslipGroupPick(pick)) {
            const selectionOdds = convertFromBposOddsV4(selection.odds);
            if (!OddsOperations.isOddsEmpty(selectionOdds) && !isEqual(selectionOdds, overAskPrice.nativeOdds, selection.odds.oddsFormat)) {
                return {
                    type: PriceType.Fixed,
                    nativeOdds: selectionOdds,
                };
            }
        }

        return undefined;
    }

    static getMarketTypeChange(overAskPick: OverAskPick, selection: SlipSelection): OverAskMarketTypeChange | undefined {
        const pick = overAskPick.pick;
        const isParticipantPick = selection.bettingOfferType === BettingOfferType.Participant && BetslipV2WinParticipantPick.isPick(pick);
        const isOptionPick = selection.bettingOfferType === BettingOfferType.Option && BetslipV2OptionMarketPick.isPick(pick);

        if (isParticipantPick || isOptionPick) {
            // Market type change can be only for Win participant bets.
            if (
                overAskPick.isEachWay &&
                (selection.participantPickDetails?.betType === ParticipantBetType.Win || selection.optionPickDetails?.betType === BetType.Win)
            ) {
                return OverAskMarketTypeChange.Win;
            }
            if (
                !overAskPick.isEachWay &&
                (selection.participantPickDetails?.betType === ParticipantBetType.EachWay || selection.optionPickDetails?.betType === BetType.EachWay)
            ) {
                return OverAskMarketTypeChange.EachWay;
            }
        }

        return undefined;
    }
}
